type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * The optional planning-detail chips, in the order they appear in the form:
 * the three from the design plus `targetWeightKg`, kept as a fourth chip so the
 * value the fattening purpose ("days to target weight") reads back stays
 * editable through this shared form (create and edit).
 */
export type HerdDetailKey =
	| "maxCapacity"
	| "formationDate"
	| "description"
	| "targetWeightKg";

export const HERD_DETAIL_KEYS: readonly HerdDetailKey[] = [
	"maxCapacity",
	"formationDate",
	"description",
	"targetWeightKg",
];

/** i18n slug under `livestock.herd.form.details.*` for each detail. */
export const HERD_DETAIL_SLUG: Record<HerdDetailKey, string> = {
	maxCapacity: "capacity",
	formationDate: "formed",
	description: "notes",
	targetWeightKg: "targetWeight",
};

export interface HerdReadbackInput {
	/** A `HerdPurpose` value; typed loosely to accept the API request shape too. */
	purpose: string;
	primaryBreed?: string | null;
	managementSystem?: string | null;
	maxCapacity?: number | null;
}

/**
 * The herd read back as one line — "Breeding · Nelore · Semi-intensive · up to
 * 180 head" — the non-empty parts joined with " · ". Shown live in the form
 * footer and again on the saved confirmation, from the one function so the two
 * can never disagree. Purpose always has a value, so the "Ready to create"
 * fallback is only a safety net.
 */
export function buildHerdReadback(input: HerdReadbackInput, t: TFn): string {
	const parts = [
		t(`livestock.herd.purposes.${input.purpose}`),
		input.primaryBreed?.trim() || null,
		input.managementSystem
			? t(`livestock.herd.managementSystems.${input.managementSystem}`)
			: null,
		input.maxCapacity != null && input.maxCapacity > 0
			? t("livestock.herd.form.upToHead", { count: input.maxCapacity })
			: null,
	].filter((part): part is string => Boolean(part));

	return parts.length > 0
		? parts.join(" · ")
		: t("livestock.herd.form.statusReady");
}

export type HerdMissingKey = "system" | HerdDetailKey;

export interface HerdMissingInput {
	managementSystem?: string | null;
	maxCapacity?: number | null;
	formationDate?: string | null;
	description?: string | null;
	targetWeightKg?: number | null;
}

/**
 * What the producer left blank, in the order the saved-state "fill in when you
 * know" card lists it: the management system first, then each empty planning
 * detail. None of these blocks creation — the card is a reminder, not an error.
 */
export function computeMissingHerdItems(
	payload: HerdMissingInput,
): HerdMissingKey[] {
	const missing: HerdMissingKey[] = [];
	if (!payload.managementSystem) missing.push("system");
	if (payload.maxCapacity == null) missing.push("maxCapacity");
	if (!payload.formationDate) missing.push("formationDate");
	if (!payload.description?.trim()) missing.push("description");
	if (payload.targetWeightKg == null) missing.push("targetWeightKg");
	return missing;
}

/** The i18n key that labels a missing item's chip in the saved-state card. */
export function herdMissingLabelKey(key: HerdMissingKey): string {
	return key === "system"
		? "livestock.herd.form.systemTitle"
		: `livestock.herd.form.details.${HERD_DETAIL_SLUG[key]}.label`;
}
